import React, { useState } from 'react';

import { dashboardArrivalNoticeHooks } from '@apis/next/arrivalNoticeApis';
import { dashboardPreAlertApis } from '@apis/next/preAlertApis';
import selectFiles from '@chenyuwang/select-files';
import { Button } from '@components/Button';
import { DevLayout } from '@components/dev/DevLayout';
import { arrivalNoticeStrings, deliveryDestinationOptions } from '@data/arrivalNotice';
import { customsRequestStrings } from '@data/customsRequest';
import { paymentSlipStrings } from '@data/paymentSlip';
import { useSearch } from '@hooks/next/useSearch';
import { useBookingInquiryDetailByD2dNo } from '@hooks/queries/dashboard/useBookingInquiryDetailByD2dNo';
import { useMutation } from '@hooks/useMutation';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { getConfig } from '@utils/config';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import ReactJson from 'react-json-view';
import { AuthManager } from '@store/AuthManager';
import { z } from 'zod';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  block: {
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: '1rem',
  },
}));

export const MockKikan: React.FC = observer(() => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(
    z.object({
      d2d_no: z.string().optional(),
    }),
  );

  const [d2d_no, set_d2d_no] = useState(search.d2d_no ?? '');

  const [customs_duty_tax_excluded, set_customs_duty_tax_excluded] = useState(0);
  const [import_consumption_tax_excluded, set_import_consumption_tax_excluded] = useState(65800);
  const [local_consumption_tax_excluded, set_local_consumption_tax_excluded] = useState(18500);

  const [sea_delivery_destination_code, set_sea_delivery_destination_code] = useState('4IDA1');

  const [fc_payment_slip_no, set_fc_payment_slip_no] = useState('');

  const [preAlertAtd, setPreAlertAtd] = useState(dayjs().format('YYYY-MM-DD'));
  const [preAlertAta, setPreAlertAta] = useState(dayjs().format('YYYY-MM-DD'));

  const preAlertQuery = dashboardPreAlertApis.useGet('/dashboard/pre-alert/:d2d_no', {
    params: {
      d2d_no,
    },
  });

  const bookingInquiryQuery = useBookingInquiryDetailByD2dNo(d2d_no);

  const arrivalNotice = dashboardArrivalNoticeHooks.useGet('/dashboard/arrival-notice/:id', {
    params: { id: preAlertQuery.data?.arrival_notice_id! },
    enabled: !!preAlertQuery.data?.arrival_notice_id,
  });

  const headers = {
    'content-type': 'application/xml',
    authorization: `${AuthManager.shared.auth_type} ${AuthManager.shared.key}`,
    accept: 'application/json',
  };

  const createDefaultInvoiceDetailPost = useMutation(async () => {
    await fetch(`${getConfig().serverUrl}/dashboard/create-default-invoice-detail/`, {
      method: 'POST',
      headers: {
        ...headers,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        d2d_no,
        customs_duty_tax_excluded,
        import_consumption_tax_excluded,
        local_consumption_tax_excluded,
      }),
    });
  });

  const patchVesselSchedule = useMutation(async (data: { ata?: string; atd?: string }) => {
    await fetch(
      `${getConfig().serverUrl}/dashboard/vessel-schedule/${preAlertQuery.data?.vessel_schedule}/`,
      {
        method: 'PATCH',
        headers: {
          ...headers,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          ata: data.ata,
          atd: data.atd,
        }),
      },
    );
  });

  const patchArrivalNoticeFC = useMutation(async () => {
    await fetch(`${getConfig().serverUrl}/dashboard/arrival-notice/${d2d_no}/`, {
      method: 'PATCH',
      headers: {
        ...headers,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        sea_delivery_destination_code: sea_delivery_destination_code,
        payment_info: {
          invoice_date: dayjs().format('YYYYMMDD'),
          invoice_no: 'AAAAAAAA',
          payment_amount: 2000,
          payment_details_count: 2,
          payment_tax: 0,
          payment_details: [
            {
              cost_id: 43,
              name: '海上運賃',
              memo: '',
              unit_price: 1000,
              unit_num: 1,
              unit_num_type: '件',
              excise_tax_type: '非課税',
              excise_tax: 0,
              total_amount: 1000,
            },
            {
              cost_id: 75,
              name: '倉庫搬出料(事前徴収)',
              memo: '',
              unit_price: 1000,
              unit_num: 1,
              unit_num_type: '件',
              excise_tax_type: '非課税',
              excise_tax: 0,
              total_amount: 1000,
            },
          ],
        },
      }),
    });
  });

  const postMoveOutSignal = useMutation(async () => {
    await fetch(`${getConfig().serverUrl}/dashboard/arrival-notice/${d2d_no}/move-out/`, {
      method: 'POST',
      headers: {
        ...headers,
        'content-type': 'application/json',
      },
      body: JSON.stringify({}),
    });
  });

  const postCustomsFile = useMutation(async (props: { txt?: File; pdf?: File }) => {
    const formData = new FormData();
    if (props.txt) {
      formData.append('txt_file', props.txt);
    }

    if (props.pdf) {
      formData.append('pdf_file', props.pdf);
    }

    await fetch(`${getConfig().serverUrl}/dashboard/customs-request/${d2d_no}/files/`, {
      method: 'POST',
      headers: {
        authorization: `${AuthManager.shared.auth_type} ${AuthManager.shared.key}`,
        accept: 'application/json',
      },
      body: formData,
    });
  });

  const putPaymentSlipPayment = useMutation(async () => {
    await fetch(`${getConfig().serverUrl}/dashboard/payment-slip/payment/`, {
      method: 'PUT',
      headers: {
        ...headers,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        d2d_no,
        fc_payment_slip_no: fc_payment_slip_no,
        actual_payment_date: dayjs().format('YYYYMMDD'),
        finish_date: dayjs().format('YYYYMMDD'),
      }),
    });
  });

  return (
    <DevLayout>
      <div className={classes.container}>
        {!AuthManager.shared.key && 'Please log in!'}
        <div className={classes.block}>
          <h4>D2D NO</h4>
          <input
            style={{ width: 120 }}
            value={d2d_no}
            onChange={(e) => {
              set_d2d_no(e.target.value);
              updateSearch({ d2d_no: e.target.value });
            }}
            onBlur={(e) => !e.target.value.startsWith('Y-') && set_d2d_no('Y-' + e.target.value)}
          />
        </div>

        <div className={classes.block}>
          <div>customs_duty_tax_excluded</div>
          <input
            style={{ width: 120 }}
            value={customs_duty_tax_excluded}
            onChange={(e) => set_customs_duty_tax_excluded(e.target.valueAsNumber)}
            type="number"
          />
          <div style={{ height: 16 }} />

          <div>import_consumption_tax_excluded</div>
          <input
            style={{ width: 120 }}
            value={import_consumption_tax_excluded}
            onChange={(e) => set_import_consumption_tax_excluded(e.target.valueAsNumber)}
          />
          <div style={{ height: 16 }} />

          <div>local_consumption_tax_excluded</div>
          <input
            style={{ width: 120 }}
            value={local_consumption_tax_excluded}
            onChange={(e) => set_local_consumption_tax_excluded(e.target.valueAsNumber)}
          />
          <div style={{ height: 16 }} />

          <Button
            color="primary"
            text="Set Invoice Details"
            size="small"
            shape="round"
            isLoading={createDefaultInvoiceDetailPost.isLoading}
            onClick={() => createDefaultInvoiceDetailPost.mutateAsync()}
          />
        </div>

        <div className={classes.block}>
          <div>ATD</div>
          <input
            type="date"
            style={{ width: 120 }}
            value={preAlertAtd}
            onChange={(e) => setPreAlertAtd(e.target.value)}
          />
          <div style={{ height: 16 }} />

          <Button
            color="primary"
            text="Set ATD"
            size="small"
            shape="round"
            isLoading={patchVesselSchedule.isLoading}
            disabled={!!preAlertQuery.data?.atd}
            onClick={() =>
              patchVesselSchedule
                .mutateAsync({ atd: preAlertAtd })
                .then(() => preAlertQuery.refetch())
            }
          />

          <div>ATA</div>
          <input
            type="date"
            style={{ width: 120 }}
            value={preAlertAta}
            onChange={(e) => setPreAlertAta(e.target.value)}
          />
          <div style={{ height: 16 }} />

          <Button
            color="primary"
            text="Set ATA"
            size="small"
            shape="round"
            isLoading={patchVesselSchedule.isLoading}
            disabled={!!preAlertQuery.data?.ata}
            onClick={() =>
              patchVesselSchedule
                .mutateAsync({ ata: preAlertAta })
                .then(() => preAlertQuery.refetch())
            }
          />
        </div>

        <div className={classes.block}>
          <div>{arrivalNoticeStrings.sea_delivery_destination}</div>
          <select
            name="delivery_destination"
            onChange={(e) => {
              set_sea_delivery_destination_code(e.currentTarget.value);
            }}
            value={sea_delivery_destination_code}
          >
            {deliveryDestinationOptions
              .filter((d) => d.delivery_destination !== 'その他')
              .map((option) => {
                return (
                  <option
                    key={option.delivery_destination}
                    value={option.delivery_destination_code}
                  >
                    {option.delivery_destination}
                  </option>
                );
              })}
          </select>
          <div style={{ height: 16 }} />
          <Button
            color="primary"
            text="Send FC Arrival Notice"
            size="small"
            shape="round"
            isLoading={patchArrivalNoticeFC.isLoading}
            onClick={() => patchArrivalNoticeFC.mutateAsync()}
            disabled={!preAlertQuery.isSuccess || !bookingInquiryQuery.isSuccess}
          />
        </div>

        <div className={classes.block}>
          <Button
            color="primary"
            text="Send Move-out OK Signal"
            size="small"
            shape="round"
            isLoading={postMoveOutSignal.isLoading}
            onClick={() => postMoveOutSignal.mutateAsync()}
            disabled={!preAlertQuery.isSuccess || !bookingInquiryQuery.isSuccess}
          />
        </div>

        <div className={classes.block}>
          <div>{paymentSlipStrings.fc_payment_slip_no}</div>
          <input
            style={{ width: 120 }}
            value={fc_payment_slip_no}
            onChange={(e) => set_fc_payment_slip_no(e.target.value)}
          />
          <div style={{ height: 16 }} />

          <Button
            color="primary"
            text="Send Payment Slip Payment Info"
            size="small"
            shape="round"
            isLoading={putPaymentSlipPayment.isLoading}
            onClick={() => putPaymentSlipPayment.mutateAsync()}
          />
        </div>

        <div className={classes.block}>
          <div>{customsRequestStrings.status}</div>
          <div style={{ height: 16 }} />
          <Button
            color="primary"
            text="Send Customs TXT File"
            size="small"
            shape="round"
            isLoading={postCustomsFile.isLoading}
            onClick={async () => {
              const files = await selectFiles({
                accept: 'text/plain',
                multiple: false,
              });

              if (files && files.length > 0) {
                const file = files[0];
                await postCustomsFile.mutateAsync({ txt: file });
              }
            }}
          />
          <div style={{ height: 16 }} />
          <Button
            color="primary"
            text="Send Customs PDF File"
            size="small"
            shape="round"
            isLoading={postCustomsFile.isLoading}
            onClick={async () => {
              const files = await selectFiles({
                accept: 'application/pdf',
                multiple: false,
              });

              if (files && files.length > 0) {
                const file = files[0];
                await postCustomsFile.mutateAsync({ pdf: file });
              }
            }}
          />
        </div>

        <div className={classes.flexInline} style={{ columnGap: '1rem' }}>
          <div>
            <h5>Pre Alert</h5>
            {preAlertQuery.data && <ReactJson src={preAlertQuery.data} collapsed={1} />}
          </div>
          <div>
            <h5>Booking Inquiry</h5>
            {bookingInquiryQuery.data && <ReactJson src={bookingInquiryQuery.data} collapsed={1} />}
          </div>
          <div>
            <h5>Arrival Notice</h5>
            {arrivalNotice.data && <ReactJson src={arrivalNotice.data} collapsed={1} />}
          </div>
        </div>
      </div>
    </DevLayout>
  );
});
